import type { ReactElement } from 'react'

interface HabitDayProgressProps {
  completedCount: number
  totalCount: number
  isDesktop?: boolean
}

export function HabitDayProgress({
  completedCount,
  totalCount,
  isDesktop = false,
}: HabitDayProgressProps): ReactElement | null {
  if (totalCount === 0) return null

  const percentage = Math.min(
    100,
    Math.round((completedCount / totalCount) * 100),
  )
  const isAllDone = completedCount === totalCount && totalCount > 0
  const remainingCount = totalCount - completedCount

  return (
    <section
      aria-label="Today's habit completion progress"
      className={`mb-4 rounded-2xl border border-gray-200 bg-white shadow-xs ${
        isDesktop ? 'p-4' : 'px-3.5 py-3'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 text-sm">
            {isAllDone ? '🎉 All Done!' : 'Today’s Progress'}
          </span>
          <span className="rounded-full bg-indigo-50 px-2 py-0.5 font-medium text-indigo-700 text-xs">
            {completedCount}/{totalCount}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isDesktop && !isAllDone && (
            <span className="text-gray-500 text-xs">
              {remainingCount} remaining
            </span>
          )}
          <span className="font-bold text-indigo-600 text-sm">
            {percentage}%
          </span>
        </div>
      </div>

      <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          tabIndex={0}
          role="progressbar"
          aria-label="Progress bar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          className={`h-full rounded-full transition-all duration-300 ${
            isAllDone ? 'bg-emerald-500' : 'bg-indigo-600'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </section>
  )
}
